const URGENCY = {
  surcote: '#A32D2D',
  dort: '#A07530',
  souscote: '#1F4D82',
  // 'rouge' = anomalie de données (annonce incomplète, modèle non renseigné…),
  // distinct de 'surcote' (rouge brique) avec un rouge plus saturé / visible.
  rouge: '#dc2626',
  // 'orange' = prix scrapé suspect ou autre alerte modérée nécessitant vérif.
  orange: '#ea580c',
}

const SEM = {
  surcote: '#A32D2D',
  dort: '#A07530',
  souscote: '#1F4D82',
  rouge: '#dc2626',
  orange: '#ea580c',
  neutral: '#64748b',
}

function clampEcart(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  if (!Number.isFinite(n)) return null
  return Math.max(-30, Math.min(30, n))
}

function microFromEcart(ecartPct) {
  const val = clampEcart(ecartPct)
  if (val == null) return { left: '50%', label: '—', dot: SEM.neutral }
  const pct = ((val + 30) / 60) * 100
  let dot = SEM.neutral
  if (val > 8) dot = SEM.surcote
  else if (val < -8) dot = SEM.souscote
  return {
    left: `${pct}%`,
    label: `${val > 0 ? '+' : ''}${val.toFixed(1)}% vs marché`,
    dot,
  }
}

function urgencySituationBadge(urgency) {
  if (urgency === 'rouge') {
    return { background: 'rgba(220,38,38,0.16)', border: '1px solid rgba(220,38,38,0.55)', color: '#fecaca' }
  }
  if (urgency === 'orange') {
    return { background: 'rgba(234,88,12,0.14)', border: '1px solid rgba(234,88,12,0.45)', color: '#fed7aa' }
  }
  if (urgency === 'surcote') {
    return { background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.35)', color: '#fecaca' }
  }
  if (urgency === 'dort') {
    return { background: 'rgba(224,184,120,0.12)', border: '1px solid rgba(224,184,120,0.35)', color: '#fde68a' }
  }
  if (urgency === 'souscote') {
    return { background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.32)', color: '#bfdbfe' }
  }
  return { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(148,163,184,0.22)', color: '#e2e8f0' }
}

/**
 * Palette dédiée aux situations concurrentielles, plus discriminante que
 * l'urgence en 4 tons. Chaque situation a sa propre couleur de pastille
 * pour identifier l'état d'un coup d'œil sans relire le libellé.
 */
const SITUATION_PALETTE = {
  critique:       { dot: '#ef4444', bg: 'rgba(239,68,68,0.10)',  border: 'rgba(239,68,68,0.40)',  fg: '#fecaca' },
  sature:         { dot: '#f59e0b', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.38)', fg: '#fde68a' },
  expose:         { dot: '#fbbf24', bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.32)', fg: '#fde68a' },
  dans_le_marche: { dot: '#94a3b8', bg: 'rgba(148,163,184,0.10)', border: 'rgba(148,163,184,0.32)', fg: '#e2e8f0' },
  avantage:       { dot: '#22c55e', bg: 'rgba(34,197,94,0.10)',  border: 'rgba(34,197,94,0.36)',  fg: '#bbf7d0' },
  exclusivite:    { dot: '#a78bfa', bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.36)', fg: '#ddd6fe' },
  // Anomalie de données (titre vide, modèle absent, données saisies par le pro
  // tellement pauvres qu'aucune analyse n'est possible).
  incomplete:     { dot: '#dc2626', bg: 'rgba(220,38,38,0.14)', border: 'rgba(220,38,38,0.55)', fg: '#fecaca' },
}
function situationBadgeStyle(situation) {
  return SITUATION_PALETTE[situation] || SITUATION_PALETTE.dans_le_marche
}

/** Petit picto textuel pour le périmètre (canton / rayon / suisse). */
function scopeGlyph(scope) {
  if (scope === 'rayon') return '◎'
  if (scope === 'suisse') return '✦'
  return '●' // canton (par défaut)
}

function positionPctLabelStyle(ecartPct, stockPanel) {
  if (ecartPct == null || ecartPct === '') {
    return { color: 'var(--text-secondary)', fontWeight: 500 }
  }
  const n = Number(ecartPct)
  if (!stockPanel || !Number.isFinite(n)) return { color: 'var(--text-secondary)', fontWeight: 500 }
  if (n > 8) return { color: '#fecaca', fontWeight: 600 }
  if (n < -8) return { color: '#bfdbfe', fontWeight: 600 }
  return { color: 'var(--text-secondary)', fontWeight: 500 }
}

/** thumb + modèle + jours/concurrents + prix + position + action */
export const VEHICLE_ROW_COLS_STOCK =
  '40px minmax(0,1.35fr) minmax(0,0.78fr) minmax(0,0.42fr) minmax(0,0.5fr) minmax(0,0.65fr)'

/** thumb + modèle + date + prix + position (sans pill) */
export const VEHICLE_ROW_COLS_MARCHE =
  '40px minmax(0,2.05fr) minmax(0,0.58fr) minmax(0,0.5fr) minmax(0,0.62fr) minmax(0,0.92fr)'

/** Marché : prix, position, délai médian vente (colonne droite). */
export const VEHICLE_ROW_COLS_MARCHE_LQ =
  '40px minmax(0,1.55fr) minmax(0,0.82fr) minmax(0,0.6fr) minmax(0,0.78fr) minmax(0,0.6fr) minmax(0,0.95fr)'

export function VehicleRow({
  rowKey,
  rowHeightPx = 72,
  gridTemplateColumns,
  mobile = false,
  urgency = 'neutral',
  imageUrl,
  fallbackLetters = '—',
  titleLine,
  metaLine,
  daysMain,
  daysSub,
  priceMain,
  priceSub,
  delayMain,
  delaySub,
  showMarcheDelay = false,
  ecartPct,
  positionSub = null,
  showPosition = true,
  showAction = true,
  actionPill,
  onRowClick,
  onPillClick,
  selectionControl = null,
  /** Une seule ligne (ellipse) pour la colonne date / métrique — ex. opportunités « écart ». */
  daysCellSingleLine = false,
  visualStyle = 'default',
  /** `badge` : sous-titre colonne « jours » en pastille (ex. statut concurrence). */
  daysSubStyle = 'plain',
  /** Lignes concurrence : situation+scope pour colorer + glypher le badge au coup d'œil. */
  daysSubSituation = null,
  daysSubScope = null,
}) {
  const cols =
    gridTemplateColumns ||
    (showAction ? VEHICLE_ROW_COLS_STOCK : showMarcheDelay ? VEHICLE_ROW_COLS_MARCHE_LQ : VEHICLE_ROW_COLS_MARCHE)
  const micro = microFromEcart(ecartPct)
  const stockPanel = visualStyle === 'stockPanel'
  const leftBorder = urgency !== 'neutral' ? `${stockPanel ? 3 : 2}px solid ${URGENCY[urgency]}` : undefined
  const outerBorder = '1px solid var(--border)'

  if (mobile) {
    return (
      <div
        role="button"
        tabIndex={0}
        data-row-key={rowKey}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onRowClick?.(e)
          }
        }}
        onClick={(e) => {
          if (e.target.closest('[data-pill]')) return
          onRowClick?.(e)
        }}
        style={{
          border: stockPanel ? '1px solid rgba(148,163,184,0.14)' : outerBorder,
          borderLeft: leftBorder || (stockPanel ? '1px solid rgba(148,163,184,0.12)' : outerBorder),
          borderRadius: stockPanel ? 6 : urgency !== 'neutral' ? '0 6px 6px 0' : 6,
          padding: stockPanel ? '12px' : '10px',
          marginBottom: stockPanel ? 10 : 8,
          background: stockPanel
            ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
            : 'rgba(255,255,255,0.02)',
          boxSizing: 'border-box',
          minWidth: 0,
          boxShadow: stockPanel ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : undefined,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr auto', gap: 10, alignItems: 'center' }}>
          <div style={{ width: 40, height: 40 }}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
                style={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 4,
                  background: 'var(--surface-alt, #20242a)',
                  boxShadow: stockPanel ? '0 0 0 1px rgba(255,255,255,0.08)' : undefined,
                }}
              />
            ) : (
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 4,
                  background: 'var(--surface-alt, #20242a)',
                  border: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                }}
              >
                {String(fallbackLetters || '—').slice(0, 4).toUpperCase()}
              </div>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.25 }}>{titleLine}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.35 }}>{metaLine}</div>
          </div>
          {showAction && actionPill ? (
            actionPill.type === 'none' ? null : (
              <button
                type="button"
                data-pill
                onClick={(e) => {
                  e.stopPropagation()
                  onPillClick?.(e)
                }}
                style={{
                  padding: stockPanel ? '7px 11px' : '5px 8px',
                  borderRadius: 4,
                  border:
                    actionPill.type === 'critical'
                      ? '1px solid rgba(163,45,45,0.45)'
                      : stockPanel
                        ? '1px solid rgba(126,166,255,0.45)'
                        : '1px solid rgba(148,163,184,0.28)',
                  background:
                    actionPill.type === 'critical'
                      ? 'rgba(127,29,29,0.2)'
                      : stockPanel
                        ? 'rgba(0,30,80,0.32)'
                        : 'rgba(255,255,255,0.03)',
                  color: actionPill.type === 'critical' ? '#fecaca' : stockPanel ? '#dbeafe' : 'var(--text-secondary)',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: stockPanel && actionPill.type !== 'critical' ? 'inset 0 1px 0 rgba(255,255,255,0.06)' : undefined,
                }}
              >
                {actionPill.label}
              </button>
            )
          ) : null}
        </div>

        <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: showMarcheDelay ? '1fr 1fr 1fr' : '1fr 1fr', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Jour/Statut</div>
            <div style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600 }}>{daysMain || '—'}</div>
        {daysSub && daysSubStyle === 'badge' ? (
          (() => {
            const palette = daysSubSituation ? situationBadgeStyle(daysSubSituation) : null
            const baseStyle = palette
              ? { background: palette.bg, border: `1px solid ${palette.border}`, color: palette.fg }
              : urgencySituationBadge(urgency)
            // Cas « annonce incomplète » : libellé court (≈ 19 chars), on force
            // une seule ligne pour qu'il reste compact à côté du prix.
            const forceSingleLine = daysSubSituation === 'incomplete'
            return (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  marginTop: 6,
                  padding: '3px 8px',
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  whiteSpace: forceSingleLine ? 'nowrap' : 'normal',
                  lineHeight: 1.25,
                  ...baseStyle,
                }}
              >
                {palette ? (
                  <span
                    aria-hidden
                    style={{
                      width: 7, height: 7, borderRadius: '50%',
                      background: palette.dot, flex: '0 0 auto',
                      boxShadow: `0 0 0 2px rgba(0,0,0,0.15)`,
                    }}
                  />
                ) : null}
                <span>{daysSub}</span>
              </div>
            )
          })()
        ) : daysSub ? (
          <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>{daysSub}</div>
        ) : null}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Prix</div>
            <div style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600 }}>{priceMain || '—'}</div>
            {priceSub ? <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>{priceSub}</div> : null}
          </div>
          {showMarcheDelay ? (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Délai</div>
              <div style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600 }}>{delayMain || '—'}</div>
              {delaySub ? <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>{delaySub}</div> : null}
            </div>
          ) : null}
        </div>

        {showPosition ? (
          <div style={{ marginTop: 8 }}>
            <div
              style={{
                position: 'relative',
                height: stockPanel ? 8 : 4,
                borderRadius: stockPanel ? 4 : 2,
                background: stockPanel
                  ? 'linear-gradient(90deg, rgba(22,101,52,0.14) 0%, rgba(148,163,184,0.1) 50%, rgba(127,29,29,0.12) 100%)'
                  : 'rgba(226,232,240,0.12)',
                marginBottom: 6,
                border: stockPanel ? '1px solid rgba(148,163,184,0.16)' : 'none',
              }}
            >
              <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: 'rgba(226,232,240,0.28)' }} />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: micro.left,
                  width: stockPanel ? 8 : 6,
                  height: stockPanel ? 8 : 6,
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: micro.dot,
                  border: stockPanel ? '2px solid rgba(248,250,252,0.9)' : '1px solid rgba(248,250,252,0.35)',
                  boxShadow: stockPanel ? '0 1px 6px rgba(0,0,0,0.35)' : undefined,
                }}
              />
            </div>
            <div style={{ fontSize: 11, ...positionPctLabelStyle(ecartPct, stockPanel) }}>{micro.label}</div>
            {positionSub ? <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>{positionSub}</div> : null}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      data-row-key={rowKey}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onRowClick?.(e)
        }
      }}
      onClick={(e) => {
        if (e.target.closest('[data-pill]')) return
        onRowClick?.(e)
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: cols,
        columnGap: stockPanel ? 12 : 10,
        alignItems: 'center',
        minHeight: stockPanel ? rowHeightPx + 8 : rowHeightPx,
        padding: stockPanel ? '8px 12px' : '0 10px',
        marginBottom: stockPanel ? 8 : 6,
        border: stockPanel ? '1px solid rgba(148,163,184,0.12)' : outerBorder,
        borderLeft: leftBorder || (stockPanel ? '1px solid rgba(148,163,184,0.12)' : outerBorder),
        borderRadius: stockPanel ? 6 : urgency !== 'neutral' ? '0 4px 4px 0' : 4,
        background: stockPanel
          ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
          : 'rgba(255,255,255,0.02)',
        cursor: onRowClick ? 'pointer' : 'default',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        boxShadow: stockPanel ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : undefined,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = stockPanel
          ? 'linear-gradient(135deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.04) 100%)'
          : 'rgba(255,255,255,0.05)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = stockPanel
          ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
          : 'rgba(255,255,255,0.02)'
      }}
    >
      {selectionControl ? <div style={{ width: 26, height: 40, flexShrink: 0 }}>{selectionControl}</div> : null}
      <div style={{ width: 40, height: 40, flexShrink: 0 }}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
            style={{
              width: 40,
              height: 40,
              objectFit: 'cover',
              borderRadius: 4,
              background: 'var(--surface-alt, #20242a)',
              display: 'block',
              boxShadow: stockPanel ? '0 0 0 1px rgba(255,255,255,0.08)' : undefined,
            }}
          />
        ) : null}
        {!imageUrl ? (
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 4,
              background: 'var(--surface-alt, #20242a)',
              border: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              letterSpacing: '0.02em',
            }}
          >
            {String(fallbackLetters || '—')
              .slice(0, 4)
              .toUpperCase()}
          </div>
        ) : null}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: stockPanel ? 15 : 14, fontWeight: stockPanel ? 600 : 500, color: 'var(--text)', lineHeight: 1.25 }}>
          {titleLine}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.25 }}>{metaLine}</div>
      </div>
      <div
        style={{
          minWidth: 0,
          ...(daysCellSingleLine
            ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
            : {}),
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{daysMain || '—'}</div>
        {daysSub && !daysCellSingleLine ? (
          daysSubStyle === 'badge' ? (
            (() => {
              const palette = daysSubSituation ? situationBadgeStyle(daysSubSituation) : null
              const baseStyle = palette
                ? { background: palette.bg, border: `1px solid ${palette.border}`, color: palette.fg }
                : urgencySituationBadge(urgency)
              const glyph = daysSubScope ? scopeGlyph(daysSubScope) : null
              // Cas « annonce incomplète » : libellé court → on évite le wrap
              // pour garder le badge compact, sur une seule ligne.
              const forceSingleLine = daysSubSituation === 'incomplete'
              return (
                <div
                  title={typeof daysSub === 'string' ? daysSub : undefined}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: forceSingleLine ? 5 : 6,
                    marginTop: 6,
                    maxWidth: '100%',
                    padding: forceSingleLine ? '2px 8px' : '3px 9px',
                    borderRadius: 999,
                    fontSize: forceSingleLine ? 10 : 11,
                    fontWeight: 600,
                    letterSpacing: '0.01em',
                    whiteSpace: forceSingleLine ? 'nowrap' : 'normal',
                    lineHeight: 1.3,
                    wordBreak: forceSingleLine ? 'normal' : 'break-word',
                    // Filet de sécurité : si le texte est plus large que la
                    // colonne, on tronque avec ellipsis plutôt que de laisser
                    // dépasser hors du badge arrondi.
                    overflow: 'hidden',
                    ...baseStyle,
                  }}
                >
                  {palette ? (
                    <span
                      aria-hidden
                      style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: palette.dot, flex: '0 0 auto',
                        boxShadow: `0 0 0 2px rgba(0,0,0,0.15)`,
                      }}
                    />
                  ) : null}
                  <span
                    style={
                      forceSingleLine
                        ? { overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }
                        : undefined
                    }
                  >
                    {daysSub}
                    {glyph ? (
                      <span
                        aria-hidden
                        style={{
                          marginLeft: 5,
                          opacity: 0.7,
                          fontSize: 9,
                          letterSpacing: 0,
                        }}
                      >
                        {glyph}
                      </span>
                    ) : null}
                  </span>
                </div>
              )
            })()
          ) : (
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {daysSub}
            </div>
          )
        ) : null}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: stockPanel ? 15 : 14, fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
          {priceMain || '—'}
        </div>
        {priceSub ? (
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-secondary)',
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {priceSub}
          </div>
        ) : null}
      </div>
      {showPosition ? (
        <div style={{ minWidth: 0 }}>
          <>
            <div
              style={{
                position: 'relative',
                height: stockPanel ? 8 : 4,
                borderRadius: stockPanel ? 4 : 2,
                background: stockPanel
                  ? 'linear-gradient(90deg, rgba(22,101,52,0.14) 0%, rgba(148,163,184,0.1) 50%, rgba(127,29,29,0.12) 100%)'
                  : 'rgba(226,232,240,0.12)',
                marginBottom: stockPanel ? 6 : 4,
                border: stockPanel ? '1px solid rgba(148,163,184,0.16)' : 'none',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: 'rgba(226,232,240,0.28)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: micro.left,
                  width: stockPanel ? 8 : 6,
                  height: stockPanel ? 8 : 6,
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: micro.dot,
                  border: stockPanel ? '2px solid rgba(248,250,252,0.9)' : '1px solid rgba(248,250,252,0.35)',
                  boxShadow: stockPanel ? '0 1px 6px rgba(0,0,0,0.35)' : undefined,
                }}
              />
            </div>
            <div
              style={{
                fontSize: 11,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ...positionPctLabelStyle(ecartPct, stockPanel),
              }}
            >
              {micro.label}
            </div>
            {positionSub ? (
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-tertiary)',
                  marginTop: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {positionSub}
              </div>
            ) : null}
          </>
        </div>
      ) : (
        <div style={{ minWidth: 0 }} />
      )}
      {showMarcheDelay ? (
        <div style={{ minWidth: 0, textAlign: 'right' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', lineHeight: 1.25 }}>{delayMain || '—'}</div>
          {delaySub ? (
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-tertiary)',
                marginTop: 2,
                lineHeight: 1.25,
                fontWeight: 500,
                letterSpacing: '0.04em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {delaySub}
            </div>
          ) : null}
        </div>
      ) : null}
      {showAction ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
          {actionPill ? (
            actionPill.type === 'none' ? (
              <span style={{ color: 'var(--text-tertiary)', fontSize: 14, fontWeight: 600 }}>-</span>
            ) : (
              <button
                type="button"
                data-pill
                onClick={(e) => {
                  e.stopPropagation()
                  onPillClick?.(e)
                }}
                style={{
                  padding: stockPanel ? '7px 11px' : '5px 8px',
                  borderRadius: 4,
                  border:
                    actionPill.type === 'critical'
                      ? '1px solid rgba(163,45,45,0.45)'
                      : stockPanel
                        ? '1px solid rgba(126,166,255,0.45)'
                        : '1px solid rgba(148,163,184,0.28)',
                  background:
                    actionPill.type === 'critical'
                      ? 'rgba(127,29,29,0.2)'
                      : stockPanel
                        ? 'rgba(0,30,80,0.32)'
                        : 'rgba(255,255,255,0.03)',
                  color: actionPill.type === 'critical' ? '#fecaca' : stockPanel ? '#dbeafe' : 'var(--text-secondary)',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  boxShadow: stockPanel && actionPill.type !== 'critical' ? 'inset 0 1px 0 rgba(255,255,255,0.06)' : undefined,
                }}
              >
                {actionPill.label}
              </button>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
